import { useState } from 'react';
import { History, User, Clock, FileText, Download, Filter } from 'lucide-react';
import { useAuditLog } from '../../hooks/useFeatureFlags';

interface AuditLogTabProps {}

export default function AuditLogTab({}: AuditLogTabProps) {
  const { logs, loading, error } = useAuditLog();
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  const filteredLogs = logs.filter(log => {
    if (filterUser && !log.userEmail.toLowerCase().includes(filterUser.toLowerCase())) {
      return false;
    }
    if (filterAction !== 'all' && log.action !== filterAction) {
      return false;
    }
    return true;
  });

  const getActionColor = (action: string) => {
    switch (action) {
      case 'enable':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'disable':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'update':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-white">Audit Log</h3>
              <p className="text-slate-400 mt-1">
                Complete history of all feature flag changes
              </p>
            </div>
            <button className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
              <Download className="h-5 w-5 mr-2" />
              Export CSV
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                placeholder="Filter by user..."
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="pl-10 pr-8 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none appearance-none"
              >
                <option value="all">All Actions</option>
                <option value="enable">Enable</option>
                <option value="disable">Disable</option>
                <option value="update">Update</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {filteredLogs.length === 0 ? (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-12 text-center">
            <History className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-400 mb-2">No Audit Logs</h3>
            <p className="text-slate-500">No changes have been recorded yet</p>
          </div>
        ) : (
          filteredLogs.map((log) => (
            <div
              key={log.id}
              className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-slate-600/50 transition-all"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-lg border text-sm font-medium ${getActionColor(log.action)}`}>
                        {log.action.toUpperCase()}
                      </span>
                      <span className="text-white font-semibold">{log.featureName}</span>
                      {log.companyId && (
                        <span className="text-slate-400 text-sm">
                          for <span className="text-purple-400">{log.companyName || log.companyId}</span>
                        </span>
                      )}
                      {!log.companyId && (
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded border border-purple-500/30">
                          GLOBAL
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{log.userEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Value Change */}
                    {(log.oldValue !== undefined || log.newValue !== undefined) && (
                      <div className="mt-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-red-400">
                            {log.oldValue !== undefined ? String(log.oldValue) : 'undefined'}
                          </span>
                          <span className="text-slate-500">→</span>
                          <span className="text-green-400">
                            {log.newValue !== undefined ? String(log.newValue) : 'undefined'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reason/Comment */}
                    {log.reason && (
                      <div className="mt-3 flex items-start gap-2">
                        <FileText className="w-4 h-4 text-slate-500 mt-0.5" />
                        <p className="text-slate-400 text-sm italic">{log.reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
